import type { ReactNode } from "react";
import { isCapacityIncomplete, type TrainingGpuCapacity } from "@/api";
import { HatTextTooltip } from "@/components/tooltip";
import styles from "./GpuCapacityPanel.module.css";

// 위젯 팝오버의 본문이다. 헤더 한 줄에 없는 정보를 펼쳐 표시한다. 여유 숫자의 분해와 대기 두 축이
// 한 화면에 있어야, 사용자가 페이지를 떠나지 않고 "왜 지금 못 쓰는지"를 판단할 수 있다.
// 분해 항목의 합은 전체와 같다:
// 여유 + 실행 중 + 예약 + 종료 예정 노드 빈 자리 + 비정상 노드 빈 자리 = 전체.
// 예열 점유는 여유 안에 이미 들어 있다.
// upstream 의 capacity 판정·노드풀·최장 대기는 표시하지 않는다. 이 값들은 실제 여유와 다른 신호를
// 준다. 예: holder(예열) pod 의 스케일업 실패도 "재고 없음"으로 판정한다.
export function GpuCapacityPanel({
  capacity,
  stale,
}: {
  capacity: TrainingGpuCapacity;
  // 마지막 조회가 실패해서 이 값이 직전 성공 결과일 때 true 다.
  stale: boolean;
}): ReactNode {
  const hasErrors = isCapacityIncomplete(capacity);

  return (
    <div className={styles.panel}>
      <div className={styles.headline}>
        <span className={styles.free}>
          여유 {capacity.free_gpus}
          <span className={styles.total}> / 전체 {capacity.total_allocatable_gpus}</span>
        </span>
      </div>

      {stale ? (
        <p className={styles.notice} role="status">
          최신 값을 받지 못해 직전 값을 보여주고 있습니다. 새로고침을 눌러 다시 시도해 주세요.
        </p>
      ) : null}

      {hasErrors ? (
        <p className={styles.warning} role="status">
          일부 값을 읽지 못해 아래 수치가 불완전합니다. 0 으로 보이는 항목이 실제로 없는 것은 아닐
          수 있습니다.
        </p>
      ) : null}

      <dl className={styles.rows}>
        <PanelRow label="실행 중" value={`${capacity.workload_running_gpus} GPU`} />
        <PanelRow label="예약으로 뗀 몫" value={`${capacity.reserved_held_aside_gpus} GPU`} />
        <PanelRow
          label={
            <HatTextTooltip
              text="노드를 미리 잡아 두는 예열 자리입니다. Training이 들어오면 비켜 주므로 여유에 포함되어 있습니다."
              placement="right"
            >
              <span className={styles.hintLabel}>예열 점유</span>
            </HatTextTooltip>
          }
          value={`${capacity.holder_running_gpus} GPU`}
        />
        <PanelRow
          label={
            <HatTextTooltip
              text="곧 회수되는 노드에 남은 빈 GPU 입니다. 새 작업을 받지 않아 여유에서 뺐고, 노드가 내려가면 전체에서도 빠집니다."
              placement="right"
            >
              <span className={styles.hintLabel}>종료 예정 노드 빈 자리</span>
            </HatTextTooltip>
          }
          value={`${capacity.retiring_idle_gpus} GPU`}
        />
        <PanelRow
          label={
            <HatTextTooltip
              text="상태가 정상이 아닌 노드에 남은 빈 GPU 입니다. 새 작업을 받지 못해 여유에서 뺐습니다. 노드가 정상으로 돌아오면 다시 여유에 포함됩니다."
              placement="right"
            >
              <span className={styles.hintLabel}>비정상 노드 빈 자리</span>
            </HatTextTooltip>
          }
          value={`${capacity.unready_idle_gpus} GPU`}
        />
        <PanelRow label="준비된 노드" value={`${capacity.ready_gpu_nodes}대`} />
      </dl>

      <div className={styles.divider} />

      <dl className={styles.rows}>
        {/* 두 대기는 단계가 다르므로 한 숫자로 합치지 않는다. job 은 k8s Workload 이고, session 은 그 전 단계다. */}
        <PanelRow label="대기 중인 job" value={`${capacity.pending_jobs}개`} />
        <PanelRow label="대기 중인 session" value={`${capacity.pending_sessions}개`} />
      </dl>
    </div>
  );
}

function PanelRow({ label, value }: { label: ReactNode; value: string }): ReactNode {
  return (
    <div className={styles.row}>
      <dt className={styles.rowLabel}>{label}</dt>
      <dd className={styles.rowValue}>{value}</dd>
    </div>
  );
}
